import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
import ReportLayout, {
  DateRangePicker,
  StatusBadge,
  ProgressBar,
} from "./components/ReportLayout";
import {
  Star,
  Gift,
  TrendingUp,
  TrendingDown,
  Coins,
  Users,
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  Building2,
  ChevronDown,
} from "lucide-react";
import useLoyaltyPointsReport from "@/hooks/useLoyaltyPointsReport";

// ============================================
// LOYALTY POINTS REPORT
// Purpose: Track loyalty points earned, redeemed, and outstanding
// ============================================

const PERIOD_MAP = {
  today: "today",
  yesterday: "yesterday",
  thisWeek: "this_week",
  thisMonth: "this_month",
  lastMonth: "last_month",
};

export default function LoyaltyPointsReport() {
  const { hasPermission } = useAuth();

  if (!hasPermission(P.REPORTS.CUSTOMERS.SCREEN)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Access Denied
          </h3>
          <p className="text-sm text-slate-500">
            You don't have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  const { data, summary: rawSummary, branchSummary, totalItems, loading, setFilters } =
    useLoyaltyPointsReport();

  // Loading & error states
  const [error, setError] = useState(null);

  const [selectedBranchId, setSelectedBranchId] = useState(null);

  const [cachedBranchOptions, setCachedBranchOptions] = useState([]);
  useEffect(() => {
    if (branchSummary && branchSummary.length > 1 && !selectedBranchId) {
      setCachedBranchOptions(branchSummary.map(b => ({ id: b.branchId, name: b.branchName || b.branchId })));
    }
  }, [branchSummary, selectedBranchId]);
  const branchOptions = cachedBranchOptions;

  const summary = useMemo(() => {
    const earned = Number(rawSummary?.pointsEarnedInPeriod ?? rawSummary?.pointsEarned ?? 0);
    const redeemed = Number(rawSummary?.pointsRedeemedInPeriod ?? rawSummary?.pointsRedeemed ?? 0);
    return {
      totalCustomersWithPoints: Number(rawSummary?.totalCustomersWithPoints ?? rawSummary?.customersWithPoints ?? 0),
      totalPointsOutstanding: Number(rawSummary?.totalPointsOutstanding ?? rawSummary?.pointsOutstanding ?? 0),
      pointsEarnedInPeriod: earned,
      pointsRedeemedInPeriod: redeemed,
      redemptionRate: Number(rawSummary?.redemptionRate ?? (earned > 0 ? (redeemed / earned * 100) : 0)),
      pointsLiabilityValue: Number(rawSummary?.pointsLiabilityValue ?? 0),
      openingBalance: Number(rawSummary?.openingBalance ?? 0),
      closingBalance: Number(rawSummary?.closingBalance ?? 0),
    };
  }, [rawSummary]);

  // State for filters

  const formatDate = (d) =>
    d instanceof Date ? d.toISOString().split("T")[0] : d;

  const today = formatDate(new Date());

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedPreset, setSelectedPreset] = useState("today");
  const [isCustomDate, setIsCustomDate] = useState(false);

  const [sortColumn, setSortColumn] = useState("closingBalance");
  const [sortDirection, setSortDirection] = useState("desc");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState("");

  const [customerSearch, setCustomerSearch] = useState("");

  const applyPreset = (preset, page = 1, size = pageSize) => {
    setCurrentPage(page);

    setFilters((prev) => ({
      ...prev,
      period: PERIOD_MAP[preset] ?? null,
      startDate: null,
      endDate: null,
      offset: (page - 1) * size,
      limit: size,
      sortColumn,
      sortDirection,
    }));
  };

  const applyCustomDate = (page = 1, size = pageSize) => {
    setCurrentPage(page);

    setFilters((prev) => ({
      ...prev,
      period: null,
      startDate,
      endDate,
      offset: (page - 1) * size,
      limit: size,
      sortColumn,
      sortDirection,
    }));
  };

  useEffect(() => {
    applyPreset("today");
  }, []);

  //export file as csv
  const exportToCsv = () => {
    if (!data?.length) return;

    const headers = columns.map((c) => `"${c.label}"`).join(",");
    const rows = data.map((r) =>
      columns.map((c) => `"${r[c.key] ?? ""}"`).join(",")
    );

    const blob = new Blob([headers, "\n", rows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "loyalty-points-report.csv";
    link.click();
  };

  // Summary cards
  const summaryCards = [
    {
      title: "Customers with Points",
      value: summary.totalCustomersWithPoints.toLocaleString(),
      subtitle: "Active members",
      icon: Users,
    },
    {
      title: "Points Outstanding",
      value: summary.totalPointsOutstanding.toLocaleString(),
      subtitle: "Total points",
      icon: Star,
    },
    {
      title: "Points Earned",
      value: `+${summary.pointsEarnedInPeriod.toLocaleString()}`,
      subtitle: "In this period",
      icon: TrendingUp,
      trend: "up",
      // trendValue: '+12% vs last period'
    },
    {
      title: "Points Redeemed",
      value: summary.pointsRedeemedInPeriod.toLocaleString(),
      subtitle: "In this period",
      icon: Gift,
    },
    {
      title: "Redemption Rate",
      value: `${summary.redemptionRate.toFixed(1)}%`,
      subtitle: "Redeemed vs earned",
      icon: Coins,
    },
    {
      title: "Liability Value",
      value: `₹${summary.pointsLiabilityValue.toLocaleString()}`,
      subtitle: "Points in rupees",
      icon: Wallet,
    },
  ];

  // Table columns
  const columns = [
    {
      key: "customerName",
      label: "Customer",
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{row.phone}</p>
        </div>
      ),
    },
    {
      key: "openingBalance",
      label: "Opening",
      sortable: true,
      align: "right",
      render: (value) => (
        <span className="text-gray-600">{value.toLocaleString()}</span>
      ),
    },
    {
      key: "pointsEarned",
      label: "Earned",
      sortable: true,
      align: "right",
      render: (value) => (
        <span className="text-green-600 font-medium flex items-center justify-end gap-1">
          <ArrowUpRight size={14} />+{value.toLocaleString()}
        </span>
      ),
    },
    {
      key: "pointsRedeemed",
      label: "Redeemed",
      sortable: true,
      align: "right",
      render: (value) =>
        value > 0 ? (
          <span className="text-red-600 font-medium flex items-center justify-end gap-1">
            <ArrowDownRight size={14} />-{value.toLocaleString()}
          </span>
        ) : (
          <span className="text-gray-300">-</span>
        ),
    },
    {
      key: "pointsExpired",
      label: "Expired",
      sortable: true,
      align: "right",
      render: (value) =>
        value > 0 ? (
          <span className="text-amber-600">-{value.toLocaleString()}</span>
        ) : (
          <span className="text-gray-300">-</span>
        ),
    },
    {
      key: "closingBalance",
      label: "Balance",
      sortable: true,
      align: "right",
      render: (value) => (
        <div className="flex items-center justify-end gap-1">
          <Star size={14} className="text-amber-500 fill-amber-500" />
          <span className="font-bold text-gray-900">
            {value.toLocaleString()}
          </span>
        </div>
      ),
    },
    {
      key: "valueInRupees",
      label: "Value (₹)",
      sortable: true,
      align: "right",
      render: (value) => (
        <span className="font-medium text-teal-700">
          ₹{value.toLocaleString()}
        </span>
      ),
    },
    {
  key: "last_activity",
  label: "Last Activity",
  sortable: true,
  render: (value, row) => (
    <div className="text-sm">
      <p className="text-gray-900">
        {value
          ? new Date(value).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "-"}
      </p>

      <p className="text-xs text-gray-400">
        {row.lastRedeemedDate
          ? `Redeemed: ${new Date(row.lastRedeemedDate).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}`
          : "No redemptions yet"}
      </p>
    </div>
  ),
},
  ];

  const handleSearch = (query) => {
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    setFilters((prev) => ({
      ...prev,
      startDate,
      endDate,
      offset: 0,
    }));
  };

  // Filters
  const filters = (
    <div className="flex items-end gap-3 flex-wrap">
      <DateRangePicker
        startDate={startDate}
        endDate={endDate}
        selectedPreset={selectedPreset}
        onPresetSelect={(preset) => {
          setSelectedPreset(preset);
          setIsCustomDate(false);
          applyPreset(preset);
        }}
        onStartDateChange={(d) => {
          setStartDate(formatDate(d));
          setSelectedPreset(null);
          setIsCustomDate(true);
        }}
        onEndDateChange={(d) => {
          setEndDate(formatDate(d));
          setSelectedPreset(null);
          setIsCustomDate(true);
        }}
      />

      <button
        disabled={!isCustomDate}
        onClick={() => applyCustomDate()}
        className={`h-9 px-5 rounded-lg text-sm font-medium ${
          isCustomDate
            ? "bg-teal-600 text-white hover:bg-teal-700"
            : "bg-gray-300 text-gray-500 cursor-not-allowed"
        }`}
      >
        Apply Filters
      </button>

      {branchOptions.length > 0 && (
        <div className="relative">
          <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <select
            value={selectedBranchId ?? ''}
            onChange={(e) => {
              const val = e.target.value || null;
              setSelectedBranchId(val);
              setCurrentPage(1);
              setFilters(prev => ({ ...prev, branchId: val || undefined, offset: 0 }));
            }}
            className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
          >
            <option value="">All Branches</option>
            {branchOptions.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      )}
    </div>
  );

  // Pagination
  const pagination = {
    currentPage,
    totalPages: Math.ceil(totalItems / pageSize),
    totalItems: totalItems,

    pageSize,
    onPageChange: setCurrentPage,
    onPageSizeChange: (size) => {
      setPageSize(size);
      setCurrentPage(1);
    },
  };
  const sortedData = useMemo(() => {
    if (!data || !sortColumn) return data;

    const sorted = [...data];
    sorted.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
      }

      return sortDirection === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });

    return sorted;
  }, [data, sortColumn, sortDirection]);

  // CLIENT-SIDE FILTERING - Search across all columns
  const filteredData = useMemo(() => {
    if (!searchQuery.trim() || !sortedData) return sortedData;
    const query = searchQuery.toLowerCase().trim();
    return sortedData.filter(row => {
      // Search in customer name
      if (String(row.customerName || '').toLowerCase().includes(query)) return true;
      // Search in phone
      if (String(row.phone || '').toLowerCase().includes(query)) return true;
      // Search in numeric fields
      const numericFields = ['openingBalance', 'pointsEarned', 'pointsRedeemed', 'pointsExpired', 'closingBalance', 'valueInRupees'];
      for (const field of numericFields) {
        if (row[field] !== undefined && row[field] !== null) {
          if (String(row[field]).includes(query) || Number(row[field]).toLocaleString().includes(query)) return true;
        }
      }
      return false;
    });
  }, [sortedData, searchQuery]);

  return (
    <ReportLayout
      title="Loyalty Points Report"
      description="Track loyalty points earned, redeemed, and outstanding for customer rewards"
      icon={Star}
      exportPermission={P.REPORTS.CUSTOMERS.LOYALTY_EXPORT}
      color="blue"
      category="Customers"
      categoryId="customer"
      summaryCards={summaryCards}
      columns={columns}
      data={filteredData}
      filters={filters}
      loading={loading}
      error={error}
      pagination={{
        currentPage,
        totalPages: Math.ceil(totalItems / pageSize),
        totalItems,
        pageSize,
        onPageChange: (page) =>
          isCustomDate
            ? applyCustomDate(page)
            : applyPreset(selectedPreset, page),
        onPageSizeChange: (size) => {
          setPageSize(size);
          isCustomDate
            ? applyCustomDate(1, size)
            : applyPreset(selectedPreset, 1, size);
        },
      }}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={(column) => {
        let nextDirection;

        if (sortColumn === column) {
          nextDirection = sortDirection === "asc" ? "desc" : "asc";
        } else {
          nextDirection = "desc";
        }

        setSortColumn(column);
        setSortDirection(nextDirection);

      }}
      onSearch={setSearchQuery}
      searchPlaceholder="Search by customer name or phone..."
      onExport={(format) => {
        if (format === "csv") exportToCsv();
      }}
      onRefresh={handleRefresh}
    >
      {/* Points Flow Summary */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200">
          <h3 className="font-semibold text-gray-900">Points Flow Analysis</h3>
          <p className="text-xs text-gray-500">
            Movement of loyalty points in this period
          </p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Opening */}
            <div className="p-4 bg-gray-50 rounded-lg text-center">
              <p className="text-xs font-medium text-gray-500 uppercase">
                Opening Balance
              </p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {summary.openingBalance?.toLocaleString()}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Points at period start
              </p>
            </div>

            {/* Earned */}
            <div className="p-4 bg-green-50 rounded-lg text-center">
              <p className="text-xs font-medium text-green-600 uppercase">
                Points Earned
              </p>
              <p className="text-2xl font-bold text-green-600 mt-2">
                +{summary.pointsEarnedInPeriod.toLocaleString()}
              </p>
              <p className="text-xs text-green-500 mt-1">From purchases</p>
            </div>

            {/* Redeemed */}
            <div className="p-4 bg-red-50 rounded-lg text-center">
              <p className="text-xs font-medium text-red-600 uppercase">
                Points Redeemed
              </p>
              <p className="text-2xl font-bold text-red-600 mt-2">
                -{summary.pointsRedeemedInPeriod.toLocaleString()}
              </p>
              <p className="text-xs text-red-500 mt-1">Used as rewards</p>
            </div>

            {/* Closing */}
            <div className="p-4 bg-amber-50 rounded-lg text-center border-2 border-amber-200">
              <p className="text-xs font-medium text-amber-600 uppercase">
                Closing Balance
              </p>
              <p className="text-2xl font-bold text-amber-600 mt-2">
                {summary.totalPointsOutstanding.toLocaleString()}
              </p>
              <p className="text-xs text-amber-500 mt-1">
                = ₹{summary.pointsLiabilityValue.toLocaleString()} liability
              </p>
            </div>
          </div>

          {/* Redemption Progress */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Redemption Rate
              </span>
              <span className="text-sm font-semibold text-gray-900">
                {summary.redemptionRate.toFixed(1)}%
              </span>
            </div>
            <ProgressBar
              value={summary.redemptionRate}
              max={100}
              color="amber"
              showLabel={false}
            />
            <p className="text-xs text-gray-500 mt-2">
              {summary.pointsRedeemedInPeriod.toLocaleString()} redeemed out of{" "}
              {summary.pointsEarnedInPeriod.toLocaleString()} earned
            </p>
          </div>
        </div>
      </div>
    </ReportLayout>
  );
}
